import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FiBell, FiCheck, FiShield, FiLoader } from 'react-icons/fi';
import CapabilityDisplay from './CapabilityDisplay';
import RequestSubmissionForm from './RequestSubmissionForm';
import ActiveRequestsPanel from './ActiveRequestsPanel';
import SessionDetailsView from './SessionDetailsView';
import FeedbackForm from './FeedbackForm';

/**
 * AlumniBrokeragePanel - Main panel for Executive Relationship Brokerage
 *
 * This is the member-facing UI for the introduction matching system.
 * Alumni can:
 * - View their capabilities (not numeric scores)
 * - Submit introduction requests
 * - Track request status
 * - View session details
 * - Submit feedback
 */

const AlumniBrokeragePanel = ({ alumniToken }) => {
  const [eligibility, setEligibility] = useState(null);
  const [requests, setRequests] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const [isLoadingEligibility, setIsLoadingEligibility] = useState(true);
  const [isLoadingRequests, setIsLoadingRequests] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [isAcceptingTerms, setIsAcceptingTerms] = useState(false);

  const [showRequestForm, setShowRequestForm] = useState(null); // null or request_type
  const [showSessionDetails, setShowSessionDetails] = useState(null); // null or session object
  const [showFeedbackForm, setShowFeedbackForm] = useState(null); // null or session_id
  const [showNotifications, setShowNotifications] = useState(false);

  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // API base URL - must match pattern used in AlumniDashboard
  const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

  // Fetch eligibility status
  const fetchEligibility = useCallback(async () => {
    if (!alumniToken) {
      setError('Not authenticated. Please log in again.');
      setIsLoadingEligibility(false);
      return;
    }

    try {
      setIsLoadingEligibility(true);
      const url = `${API_BASE}/api/brokerage/eligibility`;
      console.log('Fetching eligibility from:', url);

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${alumniToken}` },
      });

      // Get response text first to handle non-JSON responses
      const responseText = await response.text();

      if (!response.ok) {
        // Try to parse as JSON for error details
        try {
          const errorData = JSON.parse(responseText);
          throw new Error(errorData.detail || `Server error: ${response.status}`);
        } catch (parseErr) {
          // Response is not JSON (likely HTML error page)
          console.error('Non-JSON response:', responseText.substring(0, 200));
          throw new Error(`Server error: ${response.status} - Endpoint may not exist`);
        }
      }

      // Parse successful response
      const data = JSON.parse(responseText);
      setEligibility(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching eligibility:', err);
      // Instead of showing a scary error, provide a graceful default state.
      // This lets CapabilityDisplay show the normal onboarding UI
      // (e.g. "Share Your LinkedIn Profile" cards) rather than a red error box.
      setEligibility({
        matching_eligible: false,
        has_linkedin_profile: false,
        has_exec_person_link: false,
        has_score: false,
        consent_given: false,
        capabilities: {},
        capability_tier: null,
        next_step: 'Import your profile to get started',
        linkedin_profile_linked: false,
      });
      setError(null);
    } finally {
      setIsLoadingEligibility(false);
    }
  }, [alumniToken, API_BASE]);

  // Fetch requests
  const fetchRequests = useCallback(async () => {
    try {
      setIsLoadingRequests(true);
      const response = await fetch(`${API_BASE}/api/brokerage/requests`, {
        headers: { Authorization: `Bearer ${alumniToken}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch requests');
      }

      const data = await response.json();
      setRequests(data.requests || []);
    } catch (err) {
      console.error('Error fetching requests:', err);
    } finally {
      setIsLoadingRequests(false);
    }
  }, [alumniToken, API_BASE]);

  // Fetch sessions
  const fetchSessions = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/brokerage/my/sessions`, {
        headers: { Authorization: `Bearer ${alumniToken}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch sessions');
      }

      const data = await response.json();
      setSessions(data.sessions || []);
    } catch (err) {
      console.error('Error fetching sessions:', err);
    }
  }, [alumniToken, API_BASE]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/brokerage/notifications`, {
        headers: { Authorization: `Bearer ${alumniToken}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const data = await response.json();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  }, [alumniToken, API_BASE]);

  // Initial load
  useEffect(() => {
    fetchEligibility();
    fetchRequests();
    fetchSessions();
    fetchNotifications();
  }, [fetchEligibility, fetchRequests, fetchSessions, fetchNotifications]);

  // Clear messages after delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Poll for scoring completion when LinkedIn is linked but score not yet computed
  const pollingRef = useRef(null);
  const pollCountRef = useRef(0);
  const MAX_POLL_COUNT = 18; // 18 polls × 10s = 3 minutes

  useEffect(() => {
    if (eligibility?.has_linkedin_profile && !eligibility?.has_score) {
      // Start polling every 10 seconds
      pollCountRef.current = 0;
      pollingRef.current = setInterval(async () => {
        pollCountRef.current += 1;
        if (pollCountRef.current >= MAX_POLL_COUNT) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          return;
        }
        try {
          const response = await fetch(`${API_BASE}/api/brokerage/eligibility`, {
            headers: { Authorization: `Bearer ${alumniToken}` },
          });
          if (response.ok) {
            const data = await response.json();
            setEligibility(data);
            if (data.has_score) {
              // Scoring complete, stop polling
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
          }
        } catch (err) {
          console.error('Polling error:', err);
        }
      }, 10000);
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [eligibility?.has_linkedin_profile, eligibility?.has_score, alumniToken, API_BASE]);

  // Navigate to LinkedIn import in profile management
  const handleShareLinkedIn = useCallback(() => {
    window.location.href = '/alumni/profile-management#linkedin-section';
  }, []);

  // Accept matching terms
  const handleAcceptTerms = async () => {
    try {
      setIsAcceptingTerms(true);
      setError(null);

      const response = await fetch(`${API_BASE}/api/brokerage/consent`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${alumniToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accept_terms: true }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to accept terms');
      }

      setSuccessMessage('Terms accepted! You can now submit introduction requests.');
      await fetchEligibility();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsAcceptingTerms(false);
    }
  };

  // Submit introduction request
  const handleSubmitRequest = async (requestData) => {
    try {
      setIsSubmitting(true);
      setError(null);

      const response = await fetch(`${API_BASE}/api/brokerage/requests`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${alumniToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const data = await response.json();
        // Handle both string and object error details
        let errorMessage = 'Failed to submit request';
        if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (Array.isArray(data.detail)) {
          // Pydantic validation errors come as array
          errorMessage = data.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof data.detail === 'object' && data.detail !== null) {
          errorMessage = data.detail.message || data.detail.msg || JSON.stringify(data.detail);
        }
        console.error('Request submission error:', data);
        throw new Error(errorMessage);
      }

      setShowRequestForm(null);
      setSuccessMessage('Request submitted successfully! Our team will review it shortly.');
      await fetchRequests();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Withdraw request
  const handleWithdrawRequest = async (requestId) => {
    try {
      setError(null);

      const response = await fetch(`${API_BASE}/api/brokerage/requests/${requestId}/withdraw`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${alumniToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to withdraw request');
      }

      setSuccessMessage('Request withdrawn successfully.');
      await fetchRequests();
    } catch (err) {
      setError(err.message);
    }
  };

  // View session details
  const handleViewSession = (requestId) => {
    // Find the session for this request
    const session = sessions.find(s => s.intro_request_id === requestId);
    if (session) {
      setShowSessionDetails(session);
    } else {
      // Try to fetch session details
      console.log('Session not found for request:', requestId);
    }
  };

  // Submit feedback
  const handleSubmitFeedback = async (feedbackData) => {
    try {
      setIsSubmittingFeedback(true);
      setError(null);

      const response = await fetch(`${API_BASE}/api/brokerage/sessions/${feedbackData.session_id}/feedback`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${alumniToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          score: feedbackData.score,
          feedback_text: feedbackData.feedback_text,
          would_recommend: feedbackData.would_recommend,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to submit feedback');
      }

      setShowFeedbackForm(null);
      setShowSessionDetails(null);
      setSuccessMessage('Thank you for your feedback!');
      await fetchSessions();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  // Mark notifications as read
  const handleMarkNotificationsRead = async () => {
    try {
      await fetch(`${API_BASE}/api/brokerage/notifications/mark-all-read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${alumniToken}` },
      });

      await fetchNotifications();
    } catch (err) {
      console.error('Error marking notifications read:', err);
    }
  };

  // Calculate active request count
  const activeRequestCount = requests.filter(r =>
    !['CLOSED', 'EXPIRED', 'DECLINED', 'WITHDRAWN'].includes(r.status)
  ).length;

  if (isLoadingEligibility) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8">
        <div className="flex items-center justify-center gap-3">
          <FiLoader className="w-6 h-6 text-indigo-500 animate-spin" />
          <span className="text-gray-600 dark:text-gray-400">Loading your profile...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Notifications */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Executive Introductions
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Connect with fellow alumni through curated introductions
          </p>
        </div>

        {/* Notifications Bell */}
        <div className="relative">
          <button
            onClick={() => {
              setShowNotifications(!showNotifications);
              if (!showNotifications && unreadCount > 0) {
                handleMarkNotificationsRead();
              }
            }}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors relative"
          >
            <FiBell className="w-6 h-6 text-gray-600 dark:text-gray-300" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>

          {/* Notifications Dropdown */}
          {showNotifications && (
            <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-medium text-gray-900 dark:text-gray-100">Notifications</h3>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                    No notifications yet
                  </div>
                ) : (
                  notifications.slice(0, 10).map((notif) => (
                    <div
                      key={notif.id}
                      className={`p-4 border-b border-gray-100 dark:border-gray-700 last:border-0 ${
                        !notif.in_app_read ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''
                      }`}
                    >
                      <p className="font-medium text-sm text-gray-900 dark:text-gray-100">
                        {notif.title}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {notif.message}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                        {new Date(notif.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-3">
          <FiCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
          <p className="text-green-800 dark:text-green-200">{successMessage}</p>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Consent Required */}
      {eligibility?.has_score && !eligibility?.consent_given && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-indigo-200 dark:border-indigo-800 p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
              <FiShield className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Accept Matching Terms
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mt-2">
                To participate in the Executive Introduction program, please accept our matching terms.
                By accepting, you agree that NACME may share your professional profile information
                with potential connections in our alumni network.
              </p>
              <ul className="mt-3 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                <li>• Your information is only shared with consenting members</li>
                <li>• You can withdraw from the program at any time</li>
                <li>• All introductions are staff-curated for quality</li>
              </ul>
              <button
                onClick={handleAcceptTerms}
                disabled={isAcceptingTerms}
                className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-medium rounded-lg transition-colors"
              >
                {isAcceptingTerms ? 'Accepting...' : 'Accept Terms & Continue'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Capability Display */}
      <CapabilityDisplay
        eligibility={eligibility}
        onRequestIntroduction={(requestType) => setShowRequestForm(requestType)}
        onShareLinkedIn={handleShareLinkedIn}
      />

      {/* Active Requests */}
      <ActiveRequestsPanel
        requests={requests}
        onWithdraw={handleWithdrawRequest}
        onViewSession={handleViewSession}
        isLoading={isLoadingRequests}
      />

      {/* Request Submission Form Modal */}
      {showRequestForm && (
        <RequestSubmissionForm
          requestType={showRequestForm}
          onClose={() => setShowRequestForm(null)}
          onSubmit={handleSubmitRequest}
          isSubmitting={isSubmitting}
          maxRequests={eligibility?.max_concurrent_requests || 3}
          currentRequestCount={activeRequestCount}
        />
      )}

      {/* Session Details Modal */}
      {showSessionDetails && (
        <SessionDetailsView
          session={showSessionDetails}
          onClose={() => setShowSessionDetails(null)}
          onSubmitFeedback={(sessionId) => {
            setShowSessionDetails(null);
            setShowFeedbackForm(sessionId);
          }}
        />
      )}

      {/* Feedback Form Modal */}
      {showFeedbackForm && (
        <FeedbackForm
          sessionId={showFeedbackForm}
          onClose={() => setShowFeedbackForm(null)}
          onSubmit={handleSubmitFeedback}
          isSubmitting={isSubmittingFeedback}
        />
      )}
    </div>
  );
};

export default AlumniBrokeragePanel;
